import { logger } from "@/utils/logger";

import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useFormContext } from '@/contexts/FormContext';
import FinanceForm from './forms/FinanceForm';
import ContactForm from './forms/ContactForm';
import OfferForm from './forms/OfferForm';
import AppointmentForm from './forms/AppointmentForm';
import ServiceForm from './forms/ServiceForm';
import CarfaxForm from './forms/CarfaxForm';
import TradeBanner from './forms/TradeBanner';
import type { DepartmentHours } from '@/utils/messageParser/types';

interface FormRendererProps {
  formType: string;
  formData: { [key: string]: string };
  onUpdateData: (data: { [key: string]: string }) => void;
  onSubmit?: (formData: { [key: string]: string }) => void;
  currentStep: number;
  onStepChange: (step: number) => void;
  openingHours?: DepartmentHours[];
  title?: string; // Allow forms to receive their title
}

const FormRenderer: React.FC<FormRendererProps> = ({
  formType,
  formData,
  onUpdateData,
  onSubmit,
  currentStep,
  onStepChange,
  openingHours,
  title,
}) => {
  const { t } = useLanguage();
  const { carfaxVin } = useFormContext();

  // Remove trailing colon if present and normalize to lowercase
  const normalizedFormType = formType.replace(/:$/, '').toLowerCase();
  logger.log('🎨 FormRenderer: formType:', formType, '-> normalized:', normalizedFormType);

  switch (normalizedFormType) {
    case 'finance':
    case 'financev1':
      return (
        <FinanceForm
          formData={formData}
          onUpdateData={onUpdateData}
          onSubmit={onSubmit}
          currentStep={currentStep}
          onStepChange={onStepChange}
          title={title}
        />
      );
    case 'contact':
      return (
        <ContactForm
          formData={formData}
          onUpdateData={onUpdateData}
          onSubmit={onSubmit}
          currentStep={currentStep}
          onStepChange={onStepChange}
          title={title}
        />
      );
    case 'offer':
      return (
        <OfferForm
          formData={formData}
          onUpdateData={onUpdateData}
          onSubmit={onSubmit}
          title={title}
        />
      );
    case 'appointment':
      return (
        <AppointmentForm
          formData={formData}
          onUpdateData={onUpdateData}
          onSubmit={onSubmit}
          currentStep={currentStep}
          onStepChange={onStepChange}
          openingHours={openingHours}
          title={title}
        />
      );
    case 'service':
      return (
        <ServiceForm
          formData={formData}
          onUpdateData={onUpdateData}
          onSubmit={onSubmit}
          currentStep={currentStep}
          onStepChange={onStepChange}
          openingHours={openingHours}
          title={title}
        />
      );
    case 'carfax':
      // FormRenderer - rendering CarfaxForm
      return (
        <CarfaxForm
          formData={formData}
          onUpdateData={onUpdateData}
          currentStep={currentStep || 0}
          onStepChange={onStepChange || (() => {})}
          onSubmit={onSubmit}
          vehicleVin={carfaxVin}
          title={title}
        />
      );
    case 'trade':
      return (
        <TradeBanner
          onClose={() => {}}
          onSubmit={onSubmit || (() => {})}
          activeForm={formType}
        />
      );
    default:
      return <div>{t('form.default.content')}</div>;
  }
};

export default FormRenderer;
